import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { formatDistanceToNow, format } from 'date-fns';
import { Mail, Send, MessageSquare, Settings, Loader2, Plus, CheckCircle2, Phone, MessageCircle, Users, Linkedin, MoreHorizontal } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarFallback } from './ui/avatar';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import SafeHtml from './SafeHtml';
import LogInteractionModal from './LogInteractionModal';
import FileUpload from './FileUpload';

const CHANNEL_META = {
    call:     { label: 'Call',     icon: Phone,          color: 'bg-blue-100 text-blue-600' },
    whatsapp: { label: 'WhatsApp', icon: MessageCircle,   color: 'bg-green-100 text-green-600' },
    email:    { label: 'Email',    icon: Mail,            color: 'bg-purple-100 text-purple-600' },
    sms:      { label: 'SMS',      icon: MessageSquare,   color: 'bg-orange-100 text-orange-600' },
    meeting:  { label: 'Meeting',  icon: Users,           color: 'bg-red-100 text-red-600' },
    linkedin: { label: 'LinkedIn', icon: Linkedin,        color: 'bg-blue-100 text-blue-800' },
    other:    { label: 'Other',    icon: MoreHorizontal,  color: 'bg-slate-100 text-slate-500' },
};

const OUTCOME_META = {
    interested:         { label: 'Interested',         color: 'bg-green-100 text-green-800' },
    not_interested:     { label: 'Not Interested',     color: 'bg-red-100 text-red-800' },
    no_answer:          { label: 'No Answer',          color: 'bg-slate-100 text-slate-600' },
    callback_requested: { label: 'Callback Requested', color: 'bg-yellow-100 text-yellow-800' },
    follow_up_needed:   { label: 'Follow-up Needed',   color: 'bg-blue-100 text-blue-800' },
    deal_closed:        { label: 'Deal Closed',        color: 'bg-emerald-100 text-emerald-800' },
};

// Construct API_URL with proper fallback and slash handling
const getApiUrl = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (!backendUrl) {
        return '/api';
    }
    // Remove trailing slash from backend URL if present
    const baseUrl = backendUrl.replace(/\/+$/, '');
    // Ensure /api is appended with proper slash
    return `${baseUrl}/api`;
};

const API_URL = getApiUrl();

const TimelineView = ({ leadId, leadName }) => {
    const [timeline, setTimeline] = useState([]);
    const [loading, setLoading] = useState(true);
    const [noteInput, setNoteInput] = useState('');
    const [addingNote, setAddingNote] = useState(false);
    const [expandedEmails, setExpandedEmails] = useState(new Set());
    const [logInteractionOpen, setLogInteractionOpen] = useState(false);
    const noteFileUploadRef = useRef(null);

    useEffect(() => {
        fetchTimeline();
    }, [leadId]);

    const fetchTimeline = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/leads/${leadId}/timeline`);
            setTimeline(response.data);
        } catch (error) {
            console.error('Failed to fetch timeline:', error);
            toast.error('Failed to load timeline');
        } finally {
            setLoading(false);
        }
    };

    const handleAddNote = async (e) => {
        e.preventDefault();
        if (!noteInput.trim()) {
            return;
        }

        try {
            setAddingNote(true);
            const res = await axios.post(`${API_URL}/leads/${leadId}/notes`, {
                content: noteInput.trim()
            });
            const noteId = res.data.id;
            if (noteFileUploadRef.current?.hasPending()) {
                await noteFileUploadRef.current.uploadPending(leadId, 'note', noteId);
            }
            toast.success('Note added successfully');
            setNoteInput('');
            fetchTimeline(); // Refresh timeline
        } catch (error) {
            console.error('Failed to add note:', error);
            toast.error('Failed to add note');
        } finally {
            setAddingNote(false);
        }
    };

    const toggleEmailExpansion = (emailId) => {
        const newExpanded = new Set(expandedEmails);
        if (newExpanded.has(emailId)) {
            newExpanded.delete(emailId);
        } else {
            newExpanded.add(emailId);
        }
        setExpandedEmails(newExpanded);
    };

    const getInitials = (email) => {
        if (!email) return '?';
        const username = email.split('@')[0];
        if (username.includes('.')) {
            const parts = username.split('.').filter(p => p.length > 0);
            if (parts.length >= 2) {
                return (parts[0][0] + parts[1][0]).toUpperCase();
            }
        }
        return username.length >= 2 ? username.substring(0, 2).toUpperCase() : username.toUpperCase() + '?';
    };

    const formatTimestamp = (timestamp) => {
        try {
            const date = new Date(timestamp);
            const now = new Date();
            const diffHours = (now - date) / (1000 * 60 * 60);

            if (diffHours < 24) {
                return formatDistanceToNow(date, { addSuffix: true });
            } else {
                return format(date, 'MMM d, yyyy');
            }
        } catch (e) {
            return 'Unknown';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden">
            {/* Add Note + Log Interaction */}
            <div className="mb-4 pb-4 border-b border-slate-200 flex-shrink-0">
                <form onSubmit={handleAddNote} className="space-y-2">
                    <Textarea
                        value={noteInput}
                        onChange={(e) => setNoteInput(e.target.value)}
                        placeholder="Add an internal note (e.g., 'John is on vacation until Monday')..."
                        className="min-h-[80px] rounded-lg resize-none"
                        disabled={addingNote}
                    />
                    <FileUpload
                        ref={noteFileUploadRef}
                        leadId={leadId}
                        entityType="note"
                        uploadAfterCreate={true}
                        compact={true}
                    />
                    <div className="flex justify-between items-center">
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="rounded-lg"
                            onClick={() => setLogInteractionOpen(true)}
                        >
                            <Phone className="h-4 w-4 mr-2" />
                            Log Interaction
                        </Button>
                        <Button
                            type="submit"
                            size="sm"
                            disabled={addingNote || !noteInput.trim()}
                            className="rounded-lg"
                        >
                            {addingNote ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <Plus className="h-4 w-4 mr-2" />
                            )}
                            Add Note
                        </Button>
                    </div>
                </form>
            </div>

            {/* Timeline */}
            <ScrollArea className="flex-1 min-h-0 pr-4">
                {timeline.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                        <MessageSquare className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                        <p>No timeline activity yet</p>
                    </div>
                ) : (
                    <div className="relative">
                        {/* Vertical line */}
                        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200"></div>

                        <div className="space-y-6">
                            {timeline.map((item, index) => {
                                // Generate unambiguous key: combine type, explicit id presence/absence, and index
                                const hasId = item.content?.id !== undefined && item.content?.id !== null;
                                const key = hasId
                                    ? `${item.type}-id-${item.content.id}-idx-${index}`
                                    : `${item.type}-no-id-idx-${index}`;
                                return (
                                    <div key={key} className="relative flex items-start gap-4">
                                        {/* Icon/Indicator */}
                                        <div className="relative z-10 flex-shrink-0">
                                            {item.type === 'email' && (
                                                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${item.content.direction === 'outbound'
                                                    ? 'bg-blue-100 text-blue-600'
                                                    : 'bg-green-100 text-green-600'
                                                    }`}>
                                                    {item.content.direction === 'outbound' ? (
                                                        <Send className="h-5 w-5" />
                                                    ) : (
                                                        <Mail className="h-5 w-5" />
                                                    )}
                                                </div>
                                            )}
                                            {item.type === 'note' && (
                                                <Avatar className="w-12 h-12 border-2 border-white shadow-sm">
                                                    <AvatarFallback className="bg-yellow-100 text-yellow-700">
                                                        {item.actor ? getInitials(item.actor.email) : '?'}
                                                    </AvatarFallback>
                                                </Avatar>
                                            )}
                                            {item.type === 'task' && (
                                                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-green-100 text-green-600">
                                                    <CheckCircle2 className="h-5 w-5" />
                                                </div>
                                            )}
                                            {item.type === 'event' && (
                                                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-slate-100 text-slate-500">
                                                    <Settings className="h-5 w-5" />
                                                </div>
                                            )}
                                            {item.type === 'interaction' && (() => {
                                                const ch = CHANNEL_META[item.content.channel] || CHANNEL_META.other;
                                                const Icon = ch.icon;
                                                return (
                                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${ch.color}`}>
                                                        <Icon className="h-5 w-5" />
                                                    </div>
                                                );
                                            })()}
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0 pb-6">
                                            {/* Email Item */}
                                            {item.type === 'email' && (
                                                <div
                                                    className={`rounded-lg border p-4 cursor-pointer transition-all hover:shadow-md ${item.content.direction === 'outbound'
                                                        ? 'bg-blue-50 border-blue-200'
                                                        : 'bg-white border-slate-200'
                                                        }`}
                                                    onClick={() => toggleEmailExpansion(item.content.id)}
                                                >
                                                    <div className="flex items-start justify-between mb-2">
                                                        <div className="flex-1">
                                                            <p className="text-sm font-medium text-slate-900 mb-1">
                                                                {item.content.subject || 'No Subject'}
                                                            </p>
                                                            <p className="text-xs text-slate-500">
                                                                {item.content.direction === 'outbound'
                                                                    ? `Sent by ${item.actor?.email || item.content.from_email}`
                                                                    : `Received from ${leadName || item.content.from_email}`
                                                                }
                                                            </p>
                                                        </div>
                                                        <span className="text-xs text-slate-400 ml-2">
                                                            {formatTimestamp(item.timestamp)}
                                                        </span>
                                                    </div>
                                                    {expandedEmails.has(item.content.id) ? (
                                                        <div className="mt-3 pt-3 border-t border-slate-200">
                                                            <SafeHtml
                                                                content={item.content.body}
                                                                className="text-sm text-slate-700"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <p className="text-sm text-slate-600 mt-2">
                                                            {item.content.snippet}
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                            {/* Note Item */}
                                            {item.type === 'note' && (
                                                <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                                                    <div className="flex items-start justify-between mb-2">
                                                        <div className="flex items-center gap-2">
                                                            <Avatar className="w-6 h-6">
                                                                <AvatarFallback className="bg-yellow-200 text-yellow-700 text-xs">
                                                                    {item.actor ? getInitials(item.actor.email) : '?'}
                                                                </AvatarFallback>
                                                            </Avatar>
                                                            <span className="text-sm font-medium text-slate-900">
                                                                {item.actor?.email || 'Unknown'} wrote:
                                                            </span>
                                                        </div>
                                                        <span className="text-xs text-slate-400 ml-2">
                                                            {formatTimestamp(item.timestamp)}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-slate-700 whitespace-pre-wrap">
                                                        {item.content.text}
                                                    </p>
                                                </div>
                                            )}

                                            {/* Task Item */}
                                            {item.type === 'task' && (
                                                <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1">
                                                            <p className="text-sm text-slate-700 line-through">
                                                                {item.content.description}
                                                            </p>
                                                            {item.actor && (
                                                                <p className="text-xs text-slate-500 mt-1">
                                                                    Completed by {item.actor.email} on {formatTimestamp(item.timestamp)}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <span className="text-xs text-slate-400 ml-2">
                                                            {formatTimestamp(item.timestamp)}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* System Event Item */}
                                            {item.type === 'event' && (
                                                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1">
                                                            <p className="text-sm text-slate-600">
                                                                {item.content.description}
                                                            </p>
                                                            {item.actor && (
                                                                <p className="text-xs text-slate-400 mt-1">
                                                                    by {item.actor.email}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <span className="text-xs text-slate-400 ml-2">
                                                            {formatTimestamp(item.timestamp)}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Interaction Item */}
                                            {item.type === 'interaction' && (() => {
                                                const ch = CHANNEL_META[item.content.channel] || CHANNEL_META.other;
                                                const oc = OUTCOME_META[item.content.outcome] || { label: item.content.outcome, color: 'bg-slate-100 text-slate-700' };
                                                return (
                                                    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                                                        <div className="flex items-start justify-between mb-2">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className="text-sm font-medium text-slate-900">{ch.label}</span>
                                                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${oc.color}`}>{oc.label}</span>
                                                                {item.content.duration_minutes && (
                                                                    <span className="text-xs text-slate-400">{item.content.duration_minutes} min</span>
                                                                )}
                                                            </div>
                                                            <span className="text-xs text-slate-400 ml-2 flex-shrink-0">
                                                                {formatTimestamp(item.timestamp)}
                                                            </span>
                                                        </div>
                                                        {item.content.notes && (
                                                            <p className="text-sm text-slate-600 whitespace-pre-wrap">{item.content.notes}</p>
                                                        )}
                                                        {item.actor && (
                                                            <p className="text-xs text-slate-400 mt-2">Logged by {item.actor.email}</p>
                                                        )}
                                                        {item.content.follow_up_task_id && (
                                                            <p className="text-xs text-blue-600 mt-1">Follow-up task scheduled</p>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </ScrollArea>

        <LogInteractionModal
            open={logInteractionOpen}
            onClose={() => setLogInteractionOpen(false)}
            leadId={leadId}
            leadName={leadName}
            onSaved={fetchTimeline}
        />
        </div>
    );
};

export default TimelineView;

